<template>
  <block-item-card
    ref="chartCard"
    :title="data.title"
    :tooltip="$t('RiskLiquidity.Section.BankRunBenchmarking.InfoTip')"
    class="fill-height d-flex flex-column block-item-card"
  >
    <v-lazy
      :options="{threshold: 0.5}"
      transition="fade-transition"
    >
      <e-chart
        :option="option"
        :autoresize="true"
        class="chart"
        :style="`height:${height}px;width:100%`"
      />
    </v-lazy>
  </block-item-card>
</template>
<script>
import { use } from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import BlockItemCard from '@/theme/default/components/common/BlockItemCard'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'

use([BarChart, GridComponent, TooltipComponent, CanvasRenderer])

export default {
  name      : 'BankRunBenchmarkingBlock',
  components: {
    BlockItemCard
  },
  mixins: [chartsColorMixin],
  props : {
    data: {
      type    : Object,
      required: true
    },
    height: { type: Number, default: 280 }
  },
  computed: {
    colors () {
      return [
        this.themeColorRGBA('synthesis-ui-red-03'),
        this.themeColorRGBA('synthesis-ui-blue-05'),
        this.themeColorRGBA('synthesis-ui-green-03')
      ]
    },

    yTicks () {
      const vals = this.vals.map(Number)
      const minV = Math.min(...vals)
      const maxV = Math.max(...vals)

      const niceCeil = (x) => {
        if (x <= 0) return 0
        const p = Math.pow(10, Math.floor(Math.log10(x))) // power of 10
        const n = x / p // normalize to [1,10)
        const m = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10 // choose 1/2/5/10
        return m * p
      }

      // crosses zero → symmetric [-S, 0, +S]
      if (minV < 0 && maxV > 0) {
        const S = niceCeil(Math.max(Math.abs(minV), Math.abs(maxV)))
        return { min: -S, max: S, interval: S }
      }

      // all ≤ 0 → [-S, -S/2, 0]
      if (maxV <= 0) {
        const S = niceCeil(Math.abs(minV))
        return { min: -S, max: 0, interval: S / 2 }
      }

      // all ≥ 0 → [0, S/2, S]
      const S = niceCeil(maxV)
      return { min: 0, max: S, interval: S / 2 }
    },
    cats () {
      return [
        this.data.runBanksMean.name,
        this.data.currentBank.name,
        this.data.nonRunBanksMean.name
      ]
    },
    vals () {
      const n = v => (v == null ? 0 : +v)
      return [
        n(this.data.runBanksMean.percentage),
        n(this.data.currentBank.percentage),
        n(this.data.nonRunBanksMean.percentage)
      ]
    },
    option () {
      return {
        grid   : { left: 16, right: 10, top: 20, bottom: 0, containLabel: true },
        tooltip: {
          trigger  : 'item',
          formatter: p => `<b>${this.cats[p.dataIndex]}</b><br/>${p.value.toFixed(1)}%`
        },
        xAxis: {
          type     : 'category',
          data     : this.cats,
          axisTick : { show: false },
          axisLine : { show: false },
          axisLabel: { color: '#6B7280', fontSize: 12, interval: 0 }
        },
        yAxis: {
          type     : 'value',
          min      : this.yTicks.min,
          max      : this.yTicks.max,
          interval : this.yTicks.interval,
          axisLine : { show: false },
          axisTick : { show: false },
          splitLine: { show: false },
          axisLabel: {
            color    : '#6B7280',
            formatter: v => `${v}%`
          }
        },
        series: [{
          type     : 'bar',
          data     : this.vals,
          barWidth : '50%',
          itemStyle: {
            borderRadius: (p) => (p.value >= 0 ? [8, 8, 0, 0] : [0, 0, 8, 8]),
            color       : (p) => this.colors[p.dataIndex]
          },
          showBackground : true,
          backgroundStyle: {
            color       : 'rgba(235, 241, 247, 0.8)',
            borderRadius: 8
          },
          label: {
            show      : true,
            position  : 'top',
            formatter : (p) => `${p.value.toFixed(1)}%`,
            fontSize  : 12,
            fontWeight: 700,
            color     : '#233143'
          }
        }]
      }
    }
  }
}
  </script>

  <style scoped>
  .chart { width: 100%; display: block; }
  </style>
